import {AfterViewInit, Component, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {
  AlarmTypeEnum,
  BooleanAlarm,
  ContactDTO,
  MetricInstanceAlert,
  MetricInstanceAlertEntityAdditionalInfo,
  NumericAlarm,
  STATIC_ALARM_SLOT_MINUTES,
  StaticAlarm,
  StringAlarm
} from "../../../../models/diagnostic/PartitionAlarm";
import {AlarmService} from "../../../../services/alarm/alarm.service";
import {UserMetricInstance} from "../../../../models/user/Metric/UserMetricInstance";
import {DataTypeGroupEnum} from "../../../../enums/dashboard/DataTypeUnitEnum";
import {DxNumberBoxComponent, DxTextBoxComponent} from "devextreme-angular";
import {BaseComponent} from "../../../BaseComponent";
import {WeekCalendar} from "../../../../models/diagnostic/WeekCalendar";

type RenotifyMode = 'OFF' | 'EVERY_WINDOW' | 'AFTER_TIME';
const DEFAULT_COOLDOWN_SEC = 60;

@Component({
  selector: 'app-alert-form',
  templateUrl: './alert-form.component.html',
  styleUrls: ['./alert-form.component.css']
})
export class AlertFormComponent extends BaseComponent implements OnInit, AfterViewInit {

  @Output() selectedItemChange = new EventEmitter<MetricInstanceAlert>();
  @Output() saveRequest = new EventEmitter<boolean>();
  @Input() partitionId!: number | null;
  @Input() loadingData!: boolean;
  @Output() loadingDataChange = new EventEmitter<boolean>();
  @Input() formEdited: boolean = false;
  @Output() formEditedChange = new EventEmitter<boolean>();
  @Input() formValid: boolean = false;
  @Output() formValidChange = new EventEmitter<boolean>();
  @Input() editEnabled: boolean = false;
  @ViewChild('lowerNumBox') lowerNumBox?: DxNumberBoxComponent;
  @ViewChild('upperNumBox') upperNumBox?: DxNumberBoxComponent;
  @ViewChild('nameTextBox') nameTextBox?: DxTextBoxComponent;
  @ViewChild('intervalNumBox') intervalNumBox?: DxNumberBoxComponent;
  protected alarmService: AlarmService = inject(AlarmService);
  protected selectedMetricIds: number[] = [];
  protected firstLoad = true;
  protected languages: string[];
  protected selectedLangPhone: string;
  protected selectedLangEmail: string;
  protected email: string;
  protected phoneNumber: string;
  protected userMustAddContacts: boolean;
  protected userMustDeleteContacts: boolean;
  protected lowerGreaterThanUpper: boolean;
  protected calendarPopupVisible = false;
  protected calendarPopupEditMode = false;
  protected customMonitoringTime = false;
  protected readonly DEFAULT_COOLDOWN_SEC = DEFAULT_COOLDOWN_SEC;
  protected readonly renotifyModes: RenotifyMode[] = ['OFF', 'EVERY_WINDOW', 'AFTER_TIME'];
  protected invalidStaticInterval: boolean;
  protected readonly AlarmTypeEnum = AlarmTypeEnum;
  protected readonly STATIC_ALARM_SLOT_MINUTES = STATIC_ALARM_SLOT_MINUTES;
  protected readonly alarmTypeItems = [AlarmTypeEnum.THRESHOLD, AlarmTypeEnum.STATIC];

  constructor() {
    super();
    this.languages = this.gmService.localeList.map(locale => locale.locale);
    this.selectedLangPhone = 'it';
    this.selectedLangEmail = 'it';
    this.email = "";
    this.phoneNumber = "";
    this.userMustAddContacts = false;
    this.userMustDeleteContacts = false;
    this.lowerGreaterThanUpper = false;
    this.invalidStaticInterval = false;
  }

  private _selectedItem!: MetricInstanceAlert;

  get selectedItem(): MetricInstanceAlert {
    return this._selectedItem;
  }

  @Input() set selectedItem(value: MetricInstanceAlert) {
    this._selectedItem = value;
    this.initializeForm();
  }

  ngOnInit() {
    super.ngOnInit();
    this.initializeForm();
  }

  ngAfterViewInit() {
    if (this.selectedMetricIds === undefined) {
      this.checkFormValidity()
    }
  }

  initializeForm() {
    if (!!this.selectedItem) {
      this.customMonitoringTime = !!this.selectedItem.calendar;
      if (!this.selectedItem.alarmType)
        this.selectedItem.alarmType = AlarmTypeEnum.THRESHOLD;
      if (!!this.selectedItem.metricInstance) {
        this.selectedMetricIds = [this.selectedItem.metricInstance.id];
        if (this.selectedItem.metricInstance.deviceId !== undefined) {
          if (this.selectedItem.metricInstance.deviceId === null)
            this.alarmService.selectedDeviceId = -2;
          else
            this.alarmService.selectedDeviceId = this.selectedItem.metricInstance.deviceId;
        }
        this.formEdited = false;
        this.formValid = true;
        this.formValidChange.emit(this.formValid);
        this.formEditedChange.emit(this.formEdited);
        this.cdr.detectChanges();
      } else {
        this.formEdited = false;
        this.selectedMetricIds = [];
        this.alarmService.selectedDeviceId = null;
      }
    }
  }

  initializeMetricAlarm(userMetricInstance: UserMetricInstance) {
    if (!!this.selectedItem && !!userMetricInstance) {
      this.selectedItem.metricInstance = userMetricInstance;
      this.selectedItem.additionalInfo = new MetricInstanceAlertEntityAdditionalInfo();
      this.initializeAlarmDefinition();
    }
  }

  onAlarmTypeChanged() {
    this.initializeAlarmDefinition();
    this.checkFormValidity();
  }

  private initializeAlarmDefinition() {
    const additionalInfo = this.selectedItem?.additionalInfo;
    if (!additionalInfo) return;

    if (this.selectedItem.alarmType === AlarmTypeEnum.STATIC) {
      if (!additionalInfo.staticAlarm)
        additionalInfo.staticAlarm = new StaticAlarm();
      return;
    }

    switch (this.selectedItem.metricInstance?.dataTypeGroup) {
      case DataTypeGroupEnum.BOOLEAN:
        if (!additionalInfo.booleanAlarm)
          additionalInfo.booleanAlarm = new BooleanAlarm();
        break;
      case DataTypeGroupEnum.FLOAT:
      case DataTypeGroupEnum.INTEGER:
        if (!additionalInfo.numericAlarm)
          additionalInfo.numericAlarm = new NumericAlarm();
        break;
      case DataTypeGroupEnum.STRING:
        if (!additionalInfo.stringAlarm)
          additionalInfo.stringAlarm = new StringAlarm();
        break;
    }
  }

  translateAlarmType = (alarmType: AlarmTypeEnum) => {
    return this.translate.instant('ALERT.FORM.ALARM_TYPE_' + alarmType);
  };

  translateStaticMode = (alarmOnChange: boolean) => {
    return this.translate.instant(alarmOnChange ? 'ALERT.FORM.STATIC_ON_CHANGE' : 'ALERT.FORM.STATIC_ON_STABLE');
  };

  checkEmailDuplicates = (item: any) => {
    if (!!this.selectedItem && !!this.selectedItem.additionalInfo) {
      return this.selectedItem.additionalInfo.emailReceivers.findIndex(x => x.value === item.value) === -1
    }
    return false;
  };

  checkPhoneDuplicates = (item: any) => {
    if (!!this.selectedItem && !!this.selectedItem.additionalInfo) {
      return this.selectedItem.additionalInfo.phoneReceivers.findIndex(x => x.value === item.value) === -1
    }
    return false;
  };

  checkFormValidity() {
    if (!this.firstLoad && this.editEnabled)
      this.formEdited = true;
    else
      this.firstLoad = false;
    this.formValid = true;
    if (!!this.nameTextBox)
      this.nameTextBox.isValid = true;

    if (!!this.lowerNumBox && !!this.upperNumBox) {
      this.lowerNumBox.isValid = true;
      this.upperNumBox.isValid = true
    }
    if (!!this.intervalNumBox)
      this.intervalNumBox.isValid = true;

    if (!!this.selectedItem) {
      if (!this.selectedItem.name || this.selectedItem.name.trim().length === 0) {
        this.formValid = false;
        if (!!this.nameTextBox) {
          this.nameTextBox.isValid = false;
        }
      }
      this.invalidStaticInterval = false;
      const staticAlarm = this.selectedItem.additionalInfo?.staticAlarm;
      if (this.selectedItem.alarmType === AlarmTypeEnum.STATIC && !!staticAlarm) {
        const interval = staticAlarm.intervalMinutes;
        if (interval === null || interval === undefined || interval < STATIC_ALARM_SLOT_MINUTES || interval % STATIC_ALARM_SLOT_MINUTES !== 0) {
          this.formValid = false;
          this.invalidStaticInterval = true;
          if (!!this.intervalNumBox)
            this.intervalNumBox.isValid = false;
        }
      }

      this.lowerGreaterThanUpper = false;
      if (this.selectedItem.alarmType !== AlarmTypeEnum.STATIC && !!this.selectedItem.additionalInfo?.numericAlarm && !!this.lowerNumBox && !!this.upperNumBox) {

        if (this.selectedItem.additionalInfo?.numericAlarm.lowerLimitEnabled && this.selectedItem.additionalInfo?.numericAlarm.lowerValue === null) {
          this.lowerNumBox.isValid = false;
          this.formValid = false;
        }

        if (this.selectedItem.additionalInfo?.numericAlarm.upperLimitEnabled
          && this.selectedItem.additionalInfo?.numericAlarm.upperValue === null) {
          this.upperNumBox.isValid = false;
          this.formValid = false;
        }
        if (this.selectedItem.additionalInfo?.numericAlarm.lowerLimitEnabled
          && this.selectedItem.additionalInfo?.numericAlarm.upperLimitEnabled
          && this.selectedItem.additionalInfo?.numericAlarm.lowerValue > this.selectedItem.additionalInfo?.numericAlarm.upperValue) {
          this.formValid = false;
          this.lowerNumBox.isValid = false;
          this.upperNumBox.isValid = false;
          this.lowerGreaterThanUpper = true;
        }
      }
    }

    this.userMustAddContacts = false;
    this.userMustDeleteContacts = false;

    if (this.selectedItem.beginAlarmNotificationOn || this.selectedItem.endAlarmNotificationOn) {
      if (!!this.selectedItem.additionalInfo) {
        if (
          this.selectedItem.additionalInfo.phoneReceivers.length === 0
          && this.selectedItem.additionalInfo.emailReceivers.length === 0
        ) {
          this.formValid = false;
          this.userMustAddContacts = true;
        }
      }
    }

    if (!this.selectedItem.beginAlarmNotificationOn && !this.selectedItem.endAlarmNotificationOn) {
      if (!!this.selectedItem.additionalInfo) {
        if (
          this.selectedItem.additionalInfo.phoneReceivers.length > 0
          || this.selectedItem.additionalInfo.emailReceivers.length > 0
        ) {
          this.formValid = false;
          this.userMustDeleteContacts = true;
        }
      }
    }

    // "Orario personalizzato" with nothing painted would be saved as "always monitored"
    if (this.customMonitoringTime && !this.selectedItem.calendar)
      this.formValid = false;

    this.formValidChange.emit(this.formValid);
    this.formEditedChange.emit(this.formEdited);
  }

  addPhoneNumber(phoneTextBox: DxTextBoxComponent) {
    if (!!this.phoneNumber && phoneTextBox.isValid) {
      let newPhone = new ContactDTO();
      newPhone.value = this.phoneNumber;
      newPhone.language = this.selectedLangPhone;
      this.selectedItem?.additionalInfo?.phoneReceivers.push(newPhone);
      this.phoneNumber = "";

      phoneTextBox.instance.reset();
    }
  }

  addEmail(emailTextBox: DxTextBoxComponent) {
    if (!!this.email && emailTextBox.isValid) {
      let newEmail = new ContactDTO();
      newEmail.value = this.email;
      newEmail.language = this.selectedLangEmail;
      this.selectedItem?.additionalInfo?.emailReceivers.push(newEmail);
      this.email = "";

      emailTextBox.instance.reset();
    }
  }

  protected onCustomMonitoringTimeChange(custom: boolean) {
    if (custom === this.customMonitoringTime) return;
    this.customMonitoringTime = custom;
    if (!custom) this.removeCalendar();
    this.checkFormValidity();
  }

  protected openCalendarPopup() {
    this.calendarPopupEditMode = this.editEnabled;
    this.calendarPopupVisible = true;
  }

  protected onCalendarChange(calendar: WeekCalendar) {
    // BE rejects a calendar with no days: an empty selection means "always monitored"
    if (calendar.days.length > 0) {
      this.selectedItem.calendar = calendar;
      this.checkFormValidity();
    } else {
      this.removeCalendar();
    }
  }

  protected removeCalendar() {
    this.selectedItem.calendar = null;
    this.selectedItem.calendarForcedRecovery = false;
    this.selectedItem.alarmCooldownSec = null;
    this.checkFormValidity();
  }

  // alarmCooldownSec: null = never re-notify, 0 = at every window start, N = at window start if N s passed since the last notification
  protected get renotifyMode(): RenotifyMode {
    const cooldown = this.selectedItem.alarmCooldownSec;
    if (cooldown === null || cooldown === undefined) return 'OFF';
    return cooldown === 0 ? 'EVERY_WINDOW' : 'AFTER_TIME';
  }

  protected onRenotifyModeChange(mode: RenotifyMode) {
    if (mode === this.renotifyMode) return;
    this.selectedItem.alarmCooldownSec = mode === 'OFF' ? null : mode === 'EVERY_WINDOW' ? 0 : DEFAULT_COOLDOWN_SEC;
    this.checkFormValidity();
  }

  protected onCooldownChange(seconds: number | null) {
    this.selectedItem.alarmCooldownSec = Math.max(1, seconds ?? 1);
    this.checkFormValidity();
  }

  translateRenotifyMode = (mode: RenotifyMode) => this.translate.instant('ALERT.FORM.RENOTIFY_' + mode);

  translateBooleanSelector = (val: boolean | number | string) => {
    return this.appService.translateMaybeBoolean(val);
  };

  deleteEmailReceiver($event: any, index: number) {
    if (!!$event.event) {
      $event.event.stopPropagation();
      this.selectedItem.additionalInfo?.emailReceivers.splice(index, 1)
    }
  }

  deletePhoneNumberReceiver($event: any, index: number) {
    if (!!$event.event) {
      $event.event.stopPropagation();
      this.selectedItem.additionalInfo?.phoneReceivers.splice(index, 1)
    }
  }
}
